import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useRef } from 'react';
import LocationsService from '../../services/LocationsService';
import notify from '../../utils/notifier';
import { hasPrivilege } from '../../utils/privileges_utility';
import { LOCATION_PRIVILEGES } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';

const UPLOAD_TYPES = [
  { value: 'state', label: 'State' },
  { value: 'city', label: 'City' },
  { value: 'unlocode', label: 'Unlocode' },
  { value: 'location', label: 'Location' }
];

const TEMPLATE_LINKS = {
  state: '/templates/state_template.xlsx',
  city: '/templates/city_template.xlsx',
  unlocode: '/templates/unlocode_template.xlsx',
  location: '/templates/location_template.xlsx'
};

const NOTES = {
  state: `
1. Please download the above template and fill the details.
2. Yellow columns are compulsory columns.`,

  city: `
1. Please download the above template and fill the details.
2. Yellow columns are compulsory columns.`,

  unlocode: `
1. Please download the above template and fill the details.
2. Yellow columns are compulsory columns.
3. iataCode is compulsory if type is when type is AIR_TERMINAL or AIRPORT.`,

  location: `
1. Please download the above template and fill the details.
2. Yellow columns are compulsory columns.
3. Check static data if you want to create linked static data in location.
4. iataCode is compulsory if type is when type is AIR_TERMINAL or AIRPORT.`
};


const BulkUploadPage = () => {
  const navigate = useNavigate();
  const [uploadType, setUploadType] = useState('');
  const [file, setFile] = useState(null);
  const [createStaticData, setCreateStaticData] = useState(false);
  const [isExtData, setisExtData] = useState(false);
  const [batchSize, setBatchSize] = useState(1000);
  const [applicationNamespace, setApplicationNamespace] = useState('');
  const [podId, setPodId] = useState('');
  const [checkWithNamesMatch, setCheckWithNamesMatch] = useState(false);
  const [checkWithGeocodeMatch, setCheckWithGeocodeMatch] = useState(false);
  const [skipLocodeValidation, setSkipLocodeValidation] = useState(false);
  const [applicationNamespaces, setApplicationNamespaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Load application namespaces on component mount
  useEffect(() => {
    // Privilege gate for bulk upload
    if (!hasPrivilege(LOCATION_PRIVILEGES['BULK_UPLOAD'])) {
      navigate('/unauthorized');
      return;
    }

    const namespaces = JSON.parse(localStorage.getItem('_applicationNamespaces') || '[]');
    const defaultNamespace = localStorage.getItem('_defaultApplicationNamespace') || '';
    setApplicationNamespaces(namespaces);
    setApplicationNamespace(defaultNamespace);
  }, []);

  const handleTypeChange = (e) => {
    setUploadType(e.target.value);
    setCreateStaticData(false); // reset checkbox on type change
    setFile(null); // reset file on type change
    // For location type, auto-select names match by default
    setCheckWithNamesMatch(e.target.value === 'location');
    setCheckWithGeocodeMatch(false);
    setSkipLocodeValidation(false);
    setisExtData(false);
    setBatchSize(1000);
    setPodId('');
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleCheckboxChange = (e) => {
    setCreateStaticData(e.target.checked);
  };

  const handleApplicationNamespaceChange = (e) => {
    setApplicationNamespace(e.target.value);
  };

  const handleCheckWithNamesMatchChange = (e) => {
    setCheckWithNamesMatch(e.target.checked);
  };

  const handleEXTDataChange = (e) => {
    setisExtData(e.target.checked);
  };

  const handleBatchSizeChange = (e) => {
    const value = e.target.value;
    // Only allow positive integers between 1 and 100000
    if (value === '') {
      setBatchSize(1000); // Default to 1000 if empty
    } else {
      const numValue = Number(value);
      if (!isNaN(numValue) && numValue > 0 && numValue <= 100000) {
        setBatchSize(numValue);
      }
    }
  };

  const handleQuickBatchSize = (size) => {
    setBatchSize(size);
  };

  const handleCheckWithGeocodeMatchChange = (e) => {
    setCheckWithGeocodeMatch(e.target.checked);
  };

  const handleSkipLocodeValidationChange = (e) => {
    setSkipLocodeValidation(e.target.checked);
  };

  const handlePodIdChange = (e) => {
    setPodId(e.target.value);
  };

  const handleTemplateClick = (type) => {
    window.open(TEMPLATE_LINKS[type], '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enforce: for location, at least one of names/geocode match must be selected
    if (uploadType === 'location' && !(checkWithNamesMatch || checkWithGeocodeMatch)) {
      notify({ type: 'error', message: 'Please select Name match or Geocode match.' });
      return;
    }
    setLoading(true);
    
    try {
      // Get user email for notifications
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const userEmail = userDetails.emailAddress || '';
      
      let response;
      
      switch (uploadType) {
        case 'unlocode':
          response = await LocationsService.bulkUploadUnlocodes(file, userEmail);
          break;
        case 'state':
          response = await LocationsService.bulkUploadStates(file, userEmail);
          break;
        case 'city':
          response = await LocationsService.bulkUploadCities(file, userEmail);
          break;
        case 'location':
          response = await LocationsService.bulkUploadLocations(
            file,
            applicationNamespace,
            checkWithNamesMatch,
            checkWithGeocodeMatch,
            skipLocodeValidation,
            createStaticData,
            isExtData,
            batchSize,
            userEmail,
            podId.trim()
          );
          break;
        default:
          throw new Error('Invalid upload type');
      }
      
      notify({ type: 'success', message: `${response.data.jobId} job uploaded.` });
      
      // Reset file selection but preserve current upload type for quick re-upload
      setFile(null);
      setApplicationNamespace('');
      setPodId('');
      setCheckWithNamesMatch(false);
      setSkipLocodeValidation(false);
      setisExtData(false);
      setBatchSize(1000);
      setCheckWithGeocodeMatch(false);
      setCreateStaticData(false);
      
    } catch (error) {
      console.error('Upload error:', error);
      notify({ type: 'error', message: error.message || 'Upload failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Show checkbox only for location, address, zone
  const showCheckbox = ['location', 'zone', 'address'].includes(uploadType);

  return (
    <div style={{ backgroundColor: '#F2F2F2', padding: '20px 40px' }}>
      <Typography variant="h5" gutterBottom><b>Bulk Upload</b></Typography>
      <Paper style={{ padding: 20, backgroundColor: '#fff' }}>
        <form onSubmit={handleSubmit}>
          <Grid container direction="column" spacing={2}>
            
            {/* Upload Type and Select File in same row */}
            <Grid item>
              <Grid container spacing={2}>
                {/* Upload Type */}
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Upload Type</InputLabel>
                    <Select
                      value={uploadType}
                      onChange={handleTypeChange}
                      label="Upload Type"
                    >
                      <MenuItem value="" disabled>Select upload type</MenuItem>
                      {UPLOAD_TYPES.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* File Upload */}
                <Grid item xs={6}>
                  <input
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    id="bulk-upload-file"
                    type="file"
                    ref={fileInputRef}
                    onClick={() => { if (fileInputRef.current) fileInputRef.current.value = null; }}
                    onChange={handleFileChange}
                    disabled={!uploadType}
                  />
                  <label htmlFor="bulk-upload-file">
                    <Button
                      variant="contained"
                      color="default"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={!uploadType}
                      style={{ height: '56px' }} // match Select height for alignment
                    >
                      Select File
                    </Button>
                  </label>
                </Grid>
              </Grid>
            </Grid>

            {/* Show file name below if selected */}
            {file && (
              <Grid item>
                <Typography variant="body2" style={{ marginTop: 8 }} color="textSecondary">
                  Selected file: {file.name}
                </Typography>
              </Grid>
            )}

            {/* Location-specific fields */}
            {uploadType === 'location' && (
              <>
                {/* Application Namespace */}
                <Grid item>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Application Namespace</InputLabel>
                    <Select
                      value={applicationNamespace}
                      onChange={handleApplicationNamespaceChange}
                      label="Application Namespace"
                    >
                      <MenuItem value="" disabled>Select application namespace</MenuItem>
                      {applicationNamespaces.map(namespace => (
                        <MenuItem key={namespace} value={namespace}>{namespace}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Pod ID */}
                <Grid item>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Pod ID"
                    value={podId}
                    onChange={handlePodIdChange}
                    placeholder="Enter Pod ID (optional)"
                    helperText="Optional: Pod ID for processing."
                    FormHelperTextProps={{
                      style: { fontSize: '0.75rem' }
                    }}
                  />
                </Grid>

                {/* Checkboxes with reduced spacing */}
                <Grid item>
                  <Grid container direction="column" spacing={1}>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkWithNamesMatch}
                            onChange={handleCheckWithNamesMatchChange}
                            color="primary"
                          />
                        }
                        label="Check with names match"
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkWithGeocodeMatch}
                            onChange={handleCheckWithGeocodeMatchChange}
                            color="primary"
                          />
                        }
                        label="Check with geocode match"
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={skipLocodeValidation}
                            onChange={handleSkipLocodeValidationChange}
                            color="primary"
                          />
                        }
                        label="Skip locode validation"
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={createStaticData}
                            onChange={handleCheckboxChange}
                            color="primary"
                          />
                        }
                        label="Create static data"
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isExtData}
                            onChange={handleEXTDataChange}
                            color="primary"
                          />
                        }
                        label="EXT Data"
                      />
                    </Grid>
                    <Grid item>
                      <Box style={{ marginTop: '8px' }}>
                        <Typography variant="body2" style={{ marginBottom: '8px', fontWeight: 500, color: '#666' }}>
                          Batch Size
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder="Enter batch size"
                          type="number"
                          value={batchSize}
                          onChange={handleBatchSizeChange}
                          inputProps={{ 
                            min: 1,
                            max: 100000,
                            style: { fontSize: '16px' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="body2" style={{ color: '#999' }}>
                                  records
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          helperText="Number of records processed per batch (1-100,000). Recommended: 1000 for optimal performance."
                          FormHelperTextProps={{
                            style: { marginTop: '8px', fontSize: '0.75rem' }
                          }}
                          style={{ 
                            backgroundColor: '#fafafa',
                            borderRadius: '4px'
                          }}
                        />
                        <Box style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography variant="caption" style={{ color: '#666', marginRight: '4px' }}>
                            Quick select:
                          </Typography>
                          {[500, 1000, 2000, 5000].map((size) => (
                            <Chip
                              key={size}
                              label={size === 1000 ? `${size.toLocaleString()} (Recommended)` : size.toLocaleString()}
                              onClick={() => handleQuickBatchSize(size)}
                              size="small"
                              variant={batchSize === size ? "default" : "outlined"}
                              color={batchSize === size ? "primary" : "default"}
                              style={{
                                cursor: 'pointer',
                                fontWeight: batchSize === size ? 600 : size === 1000 ? 500 : 400,
                                transition: 'all 0.2s ease',
                                borderColor: size === 1000 && batchSize !== size ? '#006db3' : undefined
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Checkbox for other upload types */}
            {showCheckbox && uploadType !== 'location' && (
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createStaticData}
                      onChange={handleCheckboxChange}
                      color="primary"
                    />
                  }
                  label="Create static data"
                />
              </Grid>
            )}

            {/* Submit Button */}
            <Grid item>
              <Grid container justifyContent="flex-end">
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!file || !uploadType || (uploadType === 'location' && !applicationNamespace) || loading}
        style={{ fontWeight: 600, padding: '6px 20px' }} // adjust padding to look small & neat
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
      >
        {loading ? 'Uploading...' : 'Upload'}
      </Button>
              </Grid>
            </Grid>

            {/* Template Link */}
            {uploadType && TEMPLATE_LINKS[uploadType] && (
              <Grid item>
                <Link href="#" onClick={() => handleTemplateClick(uploadType)} style={{ fontWeight: 600 }}>
                  {UPLOAD_TYPES.find(opt => opt.value === uploadType)?.label} Template
                </Link>
              </Grid>
            )}
            
            {/* Notes */}
            {uploadType && (
              <Grid item>
                {NOTES[uploadType]
                  .trim()
                  .split("\n")
                  .map((line, index) => (
                    <Typography key={index} variant="body2" color="textSecondary">
                      {line}
                    </Typography>
                  ))}
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default BulkUploadPage; 